import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ReceiptText,
  ShieldCheck,
  CircleDollarSign,
  Target,
  Users,
  CreditCard,
  Building2,
  LogOut,
  Landmark,
  Settings as SettingsIcon,
  ShieldCheck as ShieldIcon,
  Radar,
  Heart,
  Megaphone,
  UserCircle,
  HandHeart,
  FileBarChart2,
  History,
  Globe,
  Contact2,
  QrCode,
  Camera,
  HeartHandshake,
  Zap,
  Radio,
  BarChart3,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { PlanBadge } from "@/components/StatusPill";
import Logo from "@/components/Logo";
import { NAV } from "@/constants/testIds";

import OrgSwitcher from "@/components/OrgSwitcher";

const PLATFORM_ROLES = ["super_admin", "regional_admin", "financials_admin"];
const ROLE_ALIAS = { 
  admin: "organization_admin", 
  treasurer: "supervisor",
  executive: "EXECUTIVE",
  development: "DEVELOPMENT_STAFF",
  program_lead: "PROGRAM_LEADER",
  finance: "FINANCE_STAFF",
  coordinator: "VOLUNTEER_COORDINATOR",
  volunteer_role: "VOLUNTEER",
  board_member: "BOARD_MEMBER"
};

const FULL_ORG = [
  "owner", "executive_director", "organization_admin", "account_admin",
  "supervisor", "finance_reviewer", "program_manager", "accountant",
  "field_operator", "volunteer", "board",
  "EXECUTIVE", "DEVELOPMENT_STAFF", "PROGRAM_LEADER", "FINANCE_STAFF", "VOLUNTEER_COORDINATOR", "VOLUNTEER", "BOARD_MEMBER"
];
const FIN_ROLES = ["owner", "executive_director", "organization_admin", "account_admin", "supervisor", "finance_reviewer", "accountant", "EXECUTIVE", "FINANCE_STAFF"];
const APPROVERS = ["owner", "executive_director", "organization_admin", "account_admin", "supervisor", "EXECUTIVE", "FINANCE_STAFF"];
const ADMINS_ONLY = ["owner", "executive_director", "organization_admin", "account_admin", "EXECUTIVE"];

const ITEMS = [
  { to: "/app", end: true, label: "Dashboard", icon: LayoutDashboard, roles: FULL_ORG, slug: "overview" },
  { to: "/app/contacts", label: "Unified CRM Contacts", icon: Contact2, roles: ["owner", "executive_director", "organization_admin", "account_admin", "EXECUTIVE", "DEVELOPMENT_STAFF"], slug: "contacts" },
  { to: "/app/volunteer-signup", label: "Volunteer Intake & QR", icon: QrCode, roles: ["owner", "executive_director", "organization_admin", "VOLUNTEER_COORDINATOR", "VOLUNTEER", "PROGRAM_LEADER", "EXECUTIVE"], slug: "volunteer-signup" },
  { to: "/app/expense-submission", label: "Mobile Receipt OCR", icon: Camera, roles: FULL_ORG, slug: "expense-submission" },
  { to: "/app/expense-approvals", label: "Expense Approvals", icon: ShieldCheck, roles: APPROVERS, slug: "expense-approvals" },
  { to: "/app/aid-intake", label: "Aid Case Intake", icon: HeartHandshake, roles: ["owner", "executive_director", "organization_admin", "PROGRAM_LEADER", "EXECUTIVE"], slug: "aid-intake" },
  { to: "/app/workflows", label: "Workflow Automation", icon: Zap, roles: ["owner", "executive_director", "organization_admin", "EXECUTIVE", "PROGRAM_LEADER"], slug: "workflows" },
  { to: "/app/communications", label: "Communications Hub", icon: Radio, roles: ["owner", "executive_director", "organization_admin", "DEVELOPMENT_STAFF", "PROGRAM_LEADER", "VOLUNTEER_COORDINATOR", "EXECUTIVE"], slug: "communications" },
  { to: "/app/executive-dashboard", label: "Executive Impact", icon: BarChart3, roles: ["owner", "executive_director", "organization_admin", "EXECUTIVE", "BOARD_MEMBER"], slug: "executive-dashboard" },
  { to: "/app/programs", label: "Programs", icon: Target, roles: ["owner", "executive_director", "organization_admin", "PROGRAM_LEADER", "EXECUTIVE", "BOARD_MEMBER"], slug: "programs" },
  { to: "/app/donations", label: "Donations", icon: Heart, roles: FIN_ROLES.concat(["DEVELOPMENT_STAFF"]), slug: "donations" },
  { to: "/app/campaigns", label: "Campaigns", icon: Megaphone, roles: ["owner", "executive_director", "organization_admin", "DEVELOPMENT_STAFF", "EXECUTIVE"], slug: "campaigns" },
  { to: "/app/expenses", label: "Expenses Ledger", icon: ReceiptText, roles: FIN_ROLES, slug: "expenses" },
  { to: "/app/approvals", label: "Approvals", icon: ShieldCheck, roles: APPROVERS, slug: "approvals" },
  { to: "/app/reimbursements", label: "Reimbursements", icon: CircleDollarSign, roles: FIN_ROLES, slug: "reimbursements" },
  { to: "/app/volunteers", label: "Volunteers", icon: HandHeart, roles: ["owner", "executive_director", "organization_admin", "VOLUNTEER_COORDINATOR", "PROGRAM_LEADER", "EXECUTIVE", "VOLUNTEER"], slug: "volunteers" },
  { to: "/app/members", label: "Users & Groups", icon: Users, roles: ADMINS_ONLY, slug: "members" },
  { to: "/app/board", label: "Board Center", icon: Building2, roles: ["owner", "executive_director", "organization_admin", "account_admin", "supervisor", "board", "BOARD_MEMBER", "EXECUTIVE"], slug: "board" },
  { to: "/app/streams", label: "Grants & Funds", icon: Landmark, roles: FIN_ROLES.concat(["board", "BOARD_MEMBER", "DEVELOPMENT_STAFF"]), slug: "streams" },
  { to: "/app/donors", label: "Donors", icon: UserCircle, roles: ADMINS_ONLY.concat(["finance_reviewer", "DEVELOPMENT_STAFF", "DONOR"]), slug: "donors" },
  { to: "/app/reports", label: "Reports", icon: FileBarChart2, roles: FULL_ORG, slug: "reports" },
  { to: "/app/trust-page", label: "Public Trust Page", icon: Globe, roles: ADMINS_ONLY, slug: "trust-page" },
  { to: "/app/audit-log", label: "Audit Log", icon: History, roles: ADMINS_ONLY.concat(["board", "BOARD_MEMBER", "FINANCE_STAFF"]).concat(PLATFORM_ROLES), slug: "audit-log" },
  { to: "/app/billing", label: "Billing", icon: CreditCard, roles: ADMINS_ONLY, slug: "billing" },
  { to: "/app/settings", label: "Organization Settings", icon: SettingsIcon, roles: ADMINS_ONLY, slug: "settings" },
  { to: "/app/security", label: "Security", icon: ShieldIcon, roles: FULL_ORG, slug: "security" },
  { to: "/mission-control", label: "Mission Control", icon: Radar, roles: PLATFORM_ROLES, slug: "mission-control" },
];

export default function AppSidebar() {
  const { user, org, signOut } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const rawRoles = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles : [user.role || "member"];
  const userRolesUpper = rawRoles.map((r) => (ROLE_ALIAS[r] || r || "").toUpperCase());

  const items = ITEMS.filter((i) =>
    (i.roles || []).some((r) =>
      userRolesUpper.includes(r.toUpperCase()) ||
      (userRolesUpper.some((ur) => PLATFORM_ROLES.includes(ur.toLowerCase())) && PLATFORM_ROLES.includes(r))
    )
  );

  return (
    <aside className="w-64 shrink-0 border-r border-border bg-card flex flex-col h-screen sticky top-0 font-sans">
      <div className="px-4 pt-4 pb-4 border-b border-border space-y-3">
        <Logo height={26} />
        <OrgSwitcher />
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={NAV.sidebarItem(it.slug)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-indigo-50 text-indigo-700 font-semibold"
                    : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.75} />
              {it.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-border px-3 py-3">
        <div className="px-3 py-2 text-sm">
          <div className="font-medium truncate">{user.full_name || user.email}</div>
          <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{user.role}</div>
        </div>
        <button
          data-testid={NAV.logout}
          onClick={async () => { await signOut(); navigate("/login"); }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        >
          <LogOut size={14} strokeWidth={1.75} /> Sign out
        </button>

        <div className="mt-3 pt-3 border-t border-border/60 flex items-center gap-2 px-3" data-testid="powered-by">
          <span className="text-[10px] text-muted-foreground/80">Powered By</span>
          <a
            href="https://www.itprofound.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="powered-by-link"
            className="text-[11px] font-medium text-primary hover:underline tracking-tight"
          >
            ITProFound
          </a>
        </div>
      </div>
    </aside>
  );
}
